// Exchange Collection Controller - Extends main collection functionality
class ExchangeCardPrice {
  constructor(collectionElement) {
    this.collectionElement = collectionElement;
    this.voucherValue = null;
    this.isInitialized = false;
    this.observer = null;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;

    console.log('Initializing ExchangeCardPrice');

    // Load voucher value from localStorage
    this.loadVoucherValue();
    
    // Set up mutation observer for infinite scroll
    this.setupMutationObserver();
    
    // Initial update of all existing cards
    this.updateAllCards();
    
    this.isInitialized = true;
  }

  loadVoucherValue() {
    // Check for exchange data first
    const exchangeData = localStorage.getItem('exchangeGiftCard');
    if (exchangeData) {
      try {
        const data = JSON.parse(exchangeData);
        this.voucherValue = data.originalValue * 100; // Convert to cents
        this.processType = 'exchange';
        console.log('Loaded voucher value from exchangeGiftCard:', this.voucherValue);
        return true;
      } catch (error) {
        console.error('Error parsing exchange data:', error);
      }
    }
    
    // Check for giftbox choice data
    const giftboxChoiceData = localStorage.getItem('giftboxChoice');
    if (giftboxChoiceData) {
      try {
        const data = JSON.parse(giftboxChoiceData);
        this.voucherValue = parseFloat(data.originalValue) * 100; // Convert to cents
        this.processType = 'choose_process';
        console.log('Loaded voucher value from giftboxChoice:', this.voucherValue);
        return true;
      } catch (error) {
        console.error('Error parsing giftbox choice data:', error);
      }
    }
    
    console.log('No exchange or giftbox choice data found in localStorage');
    return false;
  }

  setupMutationObserver() {
    // Watch for new product cards added via infinite scroll
    this.observer = new MutationObserver((mutations) => {
      let hasNewCards = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node contains product cards
            if (node.matches?.('product-card[data-exchange-collection="true"]') || 
                node.querySelector?.('product-card[data-exchange-collection="true"]') ||
                node.matches?.('product-card[data-giftbox-choice="true"]') || 
                node.querySelector?.('product-card[data-giftbox-choice="true"]')) {
              hasNewCards = true;
            }
          }
        });
      });
      
      if (hasNewCards) {
        console.log('New product cards detected, updating prices');
        // Small delay to ensure DOM is stable
        setTimeout(() => this.updateAllCards(), 100);
      }
    });

    // Start observing within collection element
    this.observer.observe(this.collectionElement, {
      childList: true,
      subtree: true
    });
  }

  updateAllCards() {
    if (!this.voucherValue) {
      this.hideAllPriceDifferences();
      return;
    }

    // Use efficient query selector to get all exchange product cards within collection
    const productCards = this.collectionElement.querySelectorAll('product-card[data-exchange-collection="true"]');
    
    console.log(`Updating ${productCards.length} product cards with voucher value: ${this.voucherValue}`);
    
    // Use requestAnimationFrame for smooth updates with many cards
    if (productCards.length > 50) {
      this.updateCardsInBatches(productCards);
    } else {
      productCards.forEach(card => this.updateSingleCard(card));
    }
  }

  updateCardsInBatches(productCards) {
    const batchSize = 20;
    let index = 0;

    const processBatch = () => {
      const endIndex = Math.min(index + batchSize, productCards.length);
      
      for (let i = index; i < endIndex; i++) {
        this.updateSingleCard(productCards[i]);
      }
      
      index = endIndex;
      
      if (index < productCards.length) {
        requestAnimationFrame(processBatch);
      }
    };

    requestAnimationFrame(processBatch);
  }

  updateSingleCard(card) {
    const priceElement = card.querySelector('[data-price]');
    const priceDifferenceElement = card.querySelector('[data-price-difference]');
    const giftcardElement = card.querySelector('[data-giftcard-amount]');
    
    if (!priceElement || !priceDifferenceElement) return;

    const productPrice = parseInt(priceElement.dataset.price);
    if (!productPrice) return;

    const difference = productPrice - this.voucherValue;
    
    // Handle downgrade scenario (difference < 0)
    if (difference < 0) {
      const downgradeAmount = Math.abs(difference);
      
      // If downgrade is more than 50 SEK (5000 cents), show gift card
      if (downgradeAmount > 5000) {
        // Hide price difference
        priceDifferenceElement.style.display = 'none';
        priceDifferenceElement.textContent = '';
        
        // Show gift card amount
        if (giftcardElement) {
          const formattedGiftcard = this.formatMoney(downgradeAmount);
          const giftcardText = window.theme?.strings?.product?.giftcard_amount || 'Gift Card + {amount}';
          const displayText = giftcardText.replace('{amount}', formattedGiftcard);
          
          giftcardElement.textContent = displayText;
          giftcardElement.style.display = 'block';
          giftcardElement.dataset.giftcardAmount = downgradeAmount;
        }
      } else {
        // Downgrade is 50 SEK or less, show as 0 difference
        const translationText = window.theme?.strings?.product?.difference || 'Difference: {price_difference}';
        const displayText = translationText.replace('{price_difference}', this.formatMoney(0));
        
        priceDifferenceElement.textContent = displayText;
        priceDifferenceElement.style.display = 'block';
        priceDifferenceElement.dataset.priceDifference = 0;
        
        if (giftcardElement) {
          giftcardElement.style.display = 'none';
          giftcardElement.textContent = '';
        }
      }
    } else {
      // Handle upgrade scenario (difference >= 0)
      const adjustedDifference = difference; // Show actual difference for upgrades
      const formattedDifference = this.formatMoney(adjustedDifference);
      
      const translationText = window.theme?.strings?.product?.difference || 'Difference: {price_difference}';
      const displayText = translationText.replace('{price_difference}', formattedDifference);

      priceDifferenceElement.textContent = displayText;
      priceDifferenceElement.style.display = 'block';
      priceDifferenceElement.dataset.priceDifference = adjustedDifference;
      
      // Hide gift card element
      if (giftcardElement) {
        giftcardElement.style.display = 'none';
        giftcardElement.textContent = '';
      }
    }
  }

  hideAllPriceDifferences() {
    const priceDifferenceElements = this.collectionElement.querySelectorAll('[data-exchange-collection="true"] [data-price-difference]');
    priceDifferenceElements.forEach(element => {
      element.style.display = 'none';
      element.textContent = '';
    });
    
    const giftcardElements = this.collectionElement.querySelectorAll('[data-exchange-collection="true"] [data-giftcard-amount]');
    giftcardElements.forEach(element => {
      element.style.display = 'none';
      element.textContent = '';
    });
  }

  formatMoney(amount) {
    // Use Shopify's money formatting if available, otherwise fallback
    if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
      return window.Shopify.formatMoney(amount, window.theme.moneyFormat);
    }
    
    // Simple fallback formatting
    return `$${(amount / 100).toFixed(2)}`;
  }

  // Cleanup method
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    this.isInitialized = false;
  }
}